import { generatePath, matchPath } from 'react-router-dom';

/** The SSM5 page of a VIN session. */
const SSM5_SESSION_PATH = '/vin/:vin/ssm5';

/** The freeze frame detail address of a VIN session. */
const SSM5_FREEZE_FRAMES_PATH = '/vin/:vin/ssm5/ffd';

/** The routes the SSM5 Diagnostic page is served on. */
const SSM5_PATHS: readonly string[] = ['/ssm5', SSM5_SESSION_PATH, SSM5_FREEZE_FRAMES_PATH];

/** What a freeze frame address shows, or the session address it opens instead. */
export type Ssm5FreezeFramesRoute = {
    /** The session address to open when an FFD address has no page to show. */
    redirectPath?: string;
    /** The DTC whose freeze frame page shows within the session in view. */
    target?: Ssm5FreezeFramesTarget;
};

/** The DTC whose freeze frame data a page shows. */
export type Ssm5FreezeFramesTarget = {
    /** The trouble code. */
    dtcCode: string;
    /** The module the DTC belongs to. */
    module: string;
};

/**
 * Whether a pathname is served by the SSM5 Diagnostic page.
 *
 * @param pathname - the path to test
 * @returns true when the path is one of the SSM5 routes
 */
export function isSsm5Path(pathname: string): boolean {
    return SSM5_PATHS.some((path) => matchPath(path, pathname) !== null);
}

/**
 * Build the freeze frame detail address of a DTC in a VIN session.
 *
 * @param vin - the VIN of the session
 * @param target - the DTC to show
 * @returns the FFD address, with the module first in the query
 */
export function ssm5FreezeFramesPath(vin: string, target: Ssm5FreezeFramesTarget): string {
    const query = new URLSearchParams();

    query.set('module', target.module);
    query.set('dtcCode', target.dtcCode);

    return `${generatePath(SSM5_FREEZE_FRAMES_PATH, { vin })}?${query.toString()}`;
}

/**
 * Build the SSM5 address of a VIN session.
 *
 * @param vin - the VIN of the session
 * @returns the SSM5 session address
 */
export function ssm5SessionPath(vin: string): string {
    return generatePath(SSM5_SESSION_PATH, { vin });
}

/**
 * Resolve an address to its freeze frame page or the session address to open instead.
 *
 * @param location - the pathname and query of the address
 * @param sessionVin - the VIN of the open or lost session in view, or undefined when none is in view
 * @returns the target, the redirect address, or neither for another address
 */
export function toSsm5FreezeFramesRoute(
    location: {
        /** The pathname of the address. */
        pathname: string;
        /** The query string of the address. */
        search: string;
    },
    sessionVin: string | undefined
): Ssm5FreezeFramesRoute {
    const vin = matchPath(SSM5_FREEZE_FRAMES_PATH, location.pathname)?.params.vin;

    if (vin === undefined) {
        return {};
    }

    const redirectPath = ssm5SessionPath(vin);
    const target = vin === sessionVin ? toSsm5FreezeFramesTarget(location.search) : undefined;

    return target === undefined ? { redirectPath } : { target };
}

/**
 * Read the DTC to show from an address query.
 *
 * @param search - the query string of the address
 * @returns the DTC target, or undefined when a required field is missing or blank
 */
export function toSsm5FreezeFramesTarget(search: string): Ssm5FreezeFramesTarget | undefined {
    const query = new URLSearchParams(search);
    const module = query.get('module') ?? '';
    const dtcCode = query.get('dtcCode') ?? '';
    const isComplete = [module, dtcCode].every((value) => value.trim() !== '');

    if (!isComplete) {
        return undefined;
    }

    return { dtcCode, module };
}
